import { PackageScanModesFormView } from '../view/PackageScanModesFormView';
import { templatePackageScanModesForm } from '../view/template';
import { BaseWebComponentController, WebComponentEvent } from '../../../../framework/BaseWebComponentController';
import { PackageScanModesFormWebComponent } from '../component/PackageScanModesFormWebComponent';
import { PackageScanModesFormRepository } from '../repository/PackageScanModesFormRepository';
import { PackageScanModesFormViewData } from '../types/PackageScanModesFormViewData';
import { DropdownEvents } from '../../../../ui/gzDropdown/types';
import { ScanTypes } from '../types/ScanTypes';
import {
  OnCustomScanModeOptionChanged,
  OnScanModeRadioSelectionChanged,
  PackageScanModesEvent,
} from '../types/PackageScanModesEvent';
import { Feature } from '@nirvana/shared/constants';
import { ScanModes } from '../types/ScanModes';
import { ProtectedEntitiesOptions } from '../types/ProtectedEntitiesTypes';

export class PackageScanModesFormController extends BaseWebComponentController {
  public component: PackageScanModesFormWebComponent;
  public view: PackageScanModesFormView;
  public viewConstructor: typeof PackageScanModesFormView;

  public dataRepository: PackageScanModesFormRepository;
  public dataRepositoryConstructor: typeof PackageScanModesFormRepository;
  private currentScanModeType: ScanTypes;

  constructor(component: PackageScanModesFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PackageScanModesFormView,
      viewTemplate: templatePackageScanModesForm,
      dataRepositoryConstructor: PackageScanModesFormRepository,
    });
  }

  computeEventListeners() {
    return [
      {
        element: this.view.dom.$packageScanModesFormField,
        event: 'radioSelectionChanged',
        callback: this.onScanModeRadioSelectionChanged,
      },
      {
        element: this.view.dom.$packageScanModeComputers,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onComputersDropdownOptionChanged,
      },
      {
        element: this.view.dom.$packageScanModeComputersFallbackCheckbox.Input,
        event: 'change',
        callback: this.onComputersFallbackCheckboxChanged,
      },
      {
        element: this.view.dom.$packageScanModeVirtualMachines,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onVirtualMachinesDropdownOptionChanged,
      },
      {
        element: this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input,
        event: 'change',
        callback: this.onVirtualMachinesFallbackCheckboxChanged,
      },
      {
        element: this.view.dom.$packageScanModeContainersEc2,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onEc2DropdownOptionChanged,
      },
      {
        element: this.view.dom.$packageScanModeContainersEc2FallbackCheckbox.Input,
        event: 'change',
        callback: this.onEc2FallbackCheckboxChanged,
      },
      {
        element: this.view.dom.$packageScanModeContainersAzure,
        event: DropdownEvents.OPTION_SELECTED,
        callback: this.onAzureDropdownOptionChanged,
      },
      {
        element: this.view.dom.$packageScanModeContainersAzureFallbackCheckbox.Input,
        event: 'change',
        callback: this.onAzureFallbackCheckboxChanged,
      },
      {
        element: this.view.dom.$wrapper,
        event: PackageScanModesEvent.OnInit,
        callback: this.onInit,
      },
    ];
  }

  private onInit(ev: WebComponentEvent): void {
    this.changeStateFallbackOnInitComputers(ev);
    this.changeStateFallbackOnInitVms(ev);
    this.changeStateFallbackOnInitEc2(ev);
    this.changeStateFallbackOnInitAzure(ev);
  }

  private changeStateFallbackOnInitComputers(ev: WebComponentEvent): void {
    if (ev.detail.computers) {
      const type = ev.detail.computers;
      switch (type.firstOption) {
        case ScanTypes.PrivateCloud:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeComputersFallback,
            this.view.dom.$packageScanModeComputersFallbackCheckbox,
            true
          );
          break;
        case ScanTypes.Cloud:
        case ScanTypes.Local:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeComputersFallback,
            this.view.dom.$packageScanModeComputersFallbackCheckbox,
            false
          );
          break;
      }
    }
  }

  private changeStateFallbackOnInitVms(ev: WebComponentEvent): void {
    if (ev.detail.vms) {
      const type = ev.detail.vms;
      switch (type.firstOption) {
        case ScanTypes.PrivateCloud:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeVirtualMachinesFallback,
            this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox,
            true
          );
          break;
        case ScanTypes.Cloud:
        case ScanTypes.Local:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeVirtualMachinesFallback,
            this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox,
            false
          );
          break;
      }
    }
  }

  private changeStateFallbackOnInitEc2(ev: WebComponentEvent): void {
    if (ev.detail.ec2) {
      const type = ev.detail.ec2;
      switch (type.firstOption) {
        case ScanTypes.PrivateCloud:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeContainersEc2Fallback,
            this.view.dom.$packageScanModeContainersEc2FallbackCheckbox,
            true
          );
          break;
        case ScanTypes.Cloud:
        case ScanTypes.Local:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeContainersEc2Fallback,
            this.view.dom.$packageScanModeContainersEc2FallbackCheckbox,
            false
          );
          break;
      }
    }
  }

  private changeStateFallbackOnInitAzure(ev: WebComponentEvent): void {
    if (ev.detail.azure) {
      const type = ev.detail.azure;
      switch (type.firstOption) {
        case ScanTypes.PrivateCloud:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeContainersAzureFallback,
            this.view.dom.$packageScanModeContainersAzureFallbackCheckbox,
            true
          );
          break;
        case ScanTypes.Cloud:
        case ScanTypes.Local:
          this.view.changeStateFallback(
            this.view.dom.$packageScanModeContainersAzureFallback,
            this.view.dom.$packageScanModeContainersAzureFallbackCheckbox,
            false
          );
          break;
      }
    }
  }

  private onComputersFallbackCheckboxChanged(ev: WebComponentEvent): void {
    this.view.dom.$packageScanModeComputersFallback.Input.disabled = !ev.detail.checked;
  }

  private onComputersDropdownOptionChanged(ev: WebComponentEvent): void {
    switch (ev.detail.newValue.value) {
      case ScanTypes.PrivateCloud:
        this.view.dom.$packageScanModeComputersFallback.Input.value = ScanTypes.Local;
        this.view.dom.$packageScanModeComputersFallback.Input.setSelectedOption();
        this.view.dom.$packageScanModeComputersFallbackCheckbox.Input.checked = true;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeComputersFallback,
          this.view.dom.$packageScanModeComputersFallbackCheckbox,
          true
        );
        break;
      case ScanTypes.Cloud:
      case ScanTypes.Local:
        this.view.dom.$packageScanModeComputersFallbackCheckbox.Input.checked = false;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeComputersFallback,
          this.view.dom.$packageScanModeComputersFallbackCheckbox,
          false
        );
        break;
    }
    this.dispatchEventOnCustomScanOptionModeChanged();
  }

  private onVirtualMachinesFallbackCheckboxChanged(ev: WebComponentEvent): void {
    this.view.dom.$packageScanModeVirtualMachinesFallback.Input.disabled = !ev.detail.checked;
  }

  private onVirtualMachinesDropdownOptionChanged(ev: WebComponentEvent): void {
    switch (ev.detail.newValue.value) {
      case ScanTypes.PrivateCloud:
        this.view.dom.$packageScanModeVirtualMachinesFallback.Input.value = ScanTypes.Local;
        this.view.dom.$packageScanModeVirtualMachinesFallback.Input.setSelectedOption();
        this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.checked = this.dependencies.isGZ();
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeVirtualMachinesFallback,
          this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox,
          true
        );
        break;
      case ScanTypes.Cloud:
      case ScanTypes.Local:
        this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox.Input.checked = false;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeVirtualMachinesFallback,
          this.view.dom.$packageScanModeVirtualMachinesFallbackCheckbox,
          false
        );
        break;
    }
    this.dispatchEventOnCustomScanOptionModeChanged();
  }

  private onEc2FallbackCheckboxChanged(ev: WebComponentEvent): void {
    this.view.dom.$packageScanModeContainersEc2Fallback.Input.disabled = !ev.detail.checked;
  }

  private onEc2DropdownOptionChanged(ev: WebComponentEvent): void {
    switch (ev.detail.newValue.value) {
      case ScanTypes.PrivateCloud:
        this.view.dom.$packageScanModeContainersEc2Fallback.Input.value = ScanTypes.Local;
        this.view.dom.$packageScanModeContainersEc2Fallback.Input.setSelectedOption();
        this.view.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.checked = true;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeContainersEc2Fallback,
          this.view.dom.$packageScanModeContainersEc2FallbackCheckbox,
          true
        );
        break;
      case ScanTypes.Cloud:
      case ScanTypes.Local:
        this.view.dom.$packageScanModeContainersEc2FallbackCheckbox.Input.checked = false;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeContainersEc2Fallback,
          this.view.dom.$packageScanModeContainersEc2FallbackCheckbox,
          false
        );
        break;
    }
    this.dispatchEventOnCustomScanOptionModeChanged();
  }

  private onAzureFallbackCheckboxChanged(ev: WebComponentEvent): void {
    this.view.dom.$packageScanModeContainersAzureFallback.Input.disabled = !ev.detail.checked;
  }

  private onAzureDropdownOptionChanged(ev: WebComponentEvent): void {
    switch (ev.detail.newValue.value) {
      case ScanTypes.PrivateCloud:
        this.view.dom.$packageScanModeContainersAzureFallback.Input.value = ScanTypes.Local;
        this.view.dom.$packageScanModeContainersAzureFallback.Input.setSelectedOption();
        this.view.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.checked = true;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeContainersAzureFallback,
          this.view.dom.$packageScanModeContainersAzureFallbackCheckbox,
          true
        );
        break;
      case ScanTypes.Cloud:
      case ScanTypes.Local:
        this.view.dom.$packageScanModeContainersAzureFallbackCheckbox.Input.checked = false;
        this.view.changeStateFallback(
          this.view.dom.$packageScanModeContainersAzureFallback,
          this.view.dom.$packageScanModeContainersAzureFallbackCheckbox,
          false
        );
        break;
    }
    this.dispatchEventOnCustomScanOptionModeChanged();
  }

  private async onScanModeRadioSelectionChanged(ev: WebComponentEvent): Promise<void> {
    this.view.scanModeRadioInputChanged(ev.detail.selectedValue, this.component.getFeatures());

    const scanType = ev.detail.selectedValue;
    //ensure that the event is not triggered twice
    if (!this.currentScanModeType || this.currentScanModeType !== scanType) {
      // setCustomScanModeFields to default values
      //1 when first edit of a package and switch dorm automatic to custom
      //2 every time after a company is changed
      if (scanType === ScanModes.Custom && this.component.useDefaultValues) {
        this.component.setCustomScanModeFields(this.component.getFeatures());
        this.component.useDefaultValues = false;
      }

      if (!this.currentScanModeType && scanType === ScanModes.Automatic) {
        this.component.useDefaultValues = true;
      }

      this.currentScanModeType = scanType;
    }
    this.component.dispatchEvent(
      new OnScanModeRadioSelectionChanged(
        ev.detail.selectedValue,
        this.hasCentralScan(this.view.getCustomModeOptions())
      )
    );
  }

  private dispatchEventOnCustomScanOptionModeChanged(): void {
    // this event should be triggered only on custom mode view
    // the security server assigment should be visible only on custom mode
    if (this.view.dom.$packageScanModesFormField.value === ScanModes.Custom) {
      this.component.dispatchEvent(
        new OnCustomScanModeOptionChanged(this.hasCentralScan(this.view.getCustomModeOptions()))
      );
    }
  }

  public hasCentralScan(protectedEntitiesOptions: ProtectedEntitiesOptions): boolean {
    const computers = protectedEntitiesOptions.computers.firstOption;
    const virtual = protectedEntitiesOptions.vms.firstOption;
    const ec2 = protectedEntitiesOptions.ec2.firstOption;
    const azure = protectedEntitiesOptions.azure.firstOption;

    return (
      computers === ScanTypes.PrivateCloud ||
      virtual === ScanTypes.PrivateCloud ||
      (ec2 === ScanTypes.PrivateCloud && !this.dependencies.isCloud() && this.dependencies.hasAwsIntegration()) ||
      (azure === ScanTypes.PrivateCloud && !this.dependencies.isCloud() && this.dependencies.hasAzureIntegration())
    );
  }

  public setFormFieldAutoForEdr(): void {
    this.view.setFormFieldAutoForEdr();
  }

  public setScanModeAuto(availableFeatures: Feature[]): void {
    this.view.setScanModeAuto(availableFeatures);
  }

  public setCustomScanModeFields(availableFeatures: Feature[]): void {
    this.view.setCustomScanModeFieldValues(availableFeatures);
    this.view.dispatchInitEvent();
  }

  public async getDataForView(): Promise<PackageScanModesFormViewData> {
    const scanModes = this.dataRepository.getScanModes();
    const simpleScanModes = this.dataRepository.getSimpleScanModes();
    const ec2ScanModes = this.dataRepository.getEc2ScanModes();

    return {
      scanModes: scanModes,
      simpleScanModes: simpleScanModes,
      ec2ScanModes: ec2ScanModes,
      formContext: this.component.formContext,
      features: this.component.getFeatures(),
      contextIsFrat: this.component.getContextFrat(),
    };
  }
}
